/**
 * Smoke Drive share URLs — London Smoke & Cure.
 * Website paths only. Never put Firebase Storage URLs in copied links.
 */

export const DRIVE_SHARE_KIND = {
    INTERNAL: 'internal',
    EXTERNAL: 'external',
};

export const OPERATOR_DRIVE_SHARES_COLL = 'operatorDriveShares';

const TOKEN_HEX_RE = /^[a-f0-9]{32,64}$/;

/**
 * @returns {string}
 */
export function createDriveShareToken() {
    const bytes = new Uint8Array(24);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < bytes.length; i += 1) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * @param {unknown} token
 * @returns {boolean}
 */
export function isValidDriveShareToken(token) {
    return TOKEN_HEX_RE.test(String(token || '').trim());
}

/**
 * @param {string} kind
 * @returns {boolean}
 */
export function isDriveShareKind(kind) {
    return kind === DRIVE_SHARE_KIND.INTERNAL || kind === DRIVE_SHARE_KIND.EXTERNAL;
}

/**
 * @param {string} kind
 * @param {string} token
 * @returns {string}
 */
export function buildDriveSharePath(kind, token) {
    const id = String(token || '').trim();
    if (!isValidDriveShareToken(id)) {
        throw new Error('Share token is not valid.');
    }
    if (kind === DRIVE_SHARE_KIND.INTERNAL) return `/files/internal/${id}`;
    if (kind === DRIVE_SHARE_KIND.EXTERNAL) return `/files/${id}`;
    throw new Error('Share kind must be internal or external.');
}

/**
 * @param {string} kind
 * @param {string} token
 * @param {string} [origin]
 * @returns {string}
 */
export function buildDriveShareUrl(kind, token, origin) {
    const base = String(origin || '').replace(/\/$/, '') || 'https://wholesale.londonsmokeandcure.co.uk';
    return `${base}${buildDriveSharePath(kind, token)}`;
}

/**
 * Allow only same-app Smoke Drive paths after login (no open redirect).
 * @param {unknown} raw
 * @returns {string|null}
 */
export function safeFilesNextPath(raw) {
    const next = String(raw || '').trim();
    if (!next.startsWith('/files/')) return null;
    if (next.startsWith('//') || next.includes('://')) return null;
    if (next.includes('\\') || next.includes('\n') || next.includes('\r')) return null;
    if (next.includes('..')) return null;
    if (next.length > 200) return null;
    return next;
}
